/**
 * Route guard. Every page except the login pages needs a session; a learner who still has the
 * issued password is sent to the password page first; admin pages need the admin role (the
 * API checks all of this again — this only decides what to show).
 */
import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router';
import { isApiError } from '../api/http.ts';
import type { Me } from '../api/endpoints.ts';
import { PageLoading } from '../components/ErrorScreens.tsx';
import { loginPath, useMe } from './session.ts';

export function RequireLogin({
  admin = false,
  staff = false,
}: {
  admin?: boolean;
  staff?: boolean;
}) {
  const location = useLocation();
  const me = useMe();
  const here = `${location.pathname}${location.search}`;

  if (me.isPending) return <PageLoading />;
  if (me.isError) {
    if (isApiError(me.error, 'not_logged_in')) {
      return <Navigate to={loginPath(here, admin || staff)} replace />;
    }
    return (
      <main className="page-message" role="alert">
        <p>サーバーに接続できませんでした。通信環境を確認して、再読み込みしてください。</p>
      </main>
    );
  }
  if (me.data.must_change_password && location.pathname !== '/password') {
    return <Navigate to={`/password?next=${encodeURIComponent(here)}`} replace />;
  }
  if ((admin && me.data.role !== 'admin') || (staff && me.data.role !== 'staff')) {
    return (
      <main className="page-message" role="alert">
        <h1>この画面を見る権限がありません</h1>
      </main>
    );
  }
  return <Outlet context={me.data} />;
}

/** The logged-in learner, inside a RequireLogin route. */
export function useCurrentUser(): Me {
  return useOutletContext<Me>();
}
